import { createFileRoute } from '@tanstack/react-router'

import type { Customer } from '@/api/admin-customers-types'
import { useUpdateCustomer } from '@/api/api-mutations'
import { Button } from '@/components/base/button'
import { Drawer } from '@/components/base/drawer'
import { Heading } from '@/components/base/heading'
import { toast } from '@/components/base/toast'
import { FormContainer, FormFieldGroup, useAppForm } from '@/components/form/form'
import { RouteModal, RouteModalFormBlocker, useRouteModal } from '@/components/route-modal'
import { useI18n } from '@/i18n/i18n-react'

export const Route = createFileRoute('/_authenticated/_general/customers/$customerUuid/edit')({
	component: RouteComponent,
	staticData: {
		crumb: ({ context: { L } }) => L.Customers.EditTitle()
	}
})

function RouteComponent() {
	const { customerUuid } = Route.useParams()
	return (
		<RouteModal asDrawer requiresData loader={async api => await api.getCustomer({ path: { customerUuid } })}>
			{data => <EditCustomerForm customer={data} />}
		</RouteModal>
	)
}

function EditCustomerForm({ customer }: { customer: Customer }) {
	const { L } = useI18n()
	const { handleSuccess } = useRouteModal()
	const { customerUuid } = Route.useParams()

	const updateCustomer = useUpdateCustomer()

	const form = useAppForm({
		defaultValues: {
			firstName: customer.firstName || '',
			lastName: customer.lastName || '',
			email: customer.email || '',
			phone: customer.phone || '',
			ssn: customer.ssn || '',
			externalId: customer.externalId || ''
		},
		onSubmit: async ({ value }) => {
			await updateCustomer({
				path: { customerUuid },
				body: value
			})
			toast.success(L.Customers.UpdateSuccess())
			handleSuccess()
		}
	})

	return (
		<form.AppForm>
			<RouteModalFormBlocker form={form as any} />
			<Drawer.Header>
				<Drawer.Title asChild>
					<Heading>{L.Customers.EditTitle()}</Heading>
				</Drawer.Title>
			</Drawer.Header>
			<Drawer.Body className='flex-1 overflow-auto'>
				<FormContainer>
					<div className='grid grid-cols-2 gap-4'>
						<FormFieldGroup>
							<form.AppField
								name='firstName'
								children={field => (
									<field.TextField
										label={L.Fields.FirstName()}
										placeholder={L.Customers.Edit.FirstNamePlaceholder()}
									/>
								)}
							/>
						</FormFieldGroup>

						<FormFieldGroup>
							<form.AppField
								name='lastName'
								children={field => (
									<field.TextField
										label={L.Fields.LastName()}
										placeholder={L.Customers.Edit.LastNamePlaceholder()}
									/>
								)}
							/>
						</FormFieldGroup>
					</div>

					<FormFieldGroup>
						<form.AppField
							name='email'
							children={field => (
								<field.TextField
									label={L.Fields.Email()}
									type='email'
									placeholder={L.Customers.Edit.EmailPlaceholder()}
								/>
							)}
						/>
					</FormFieldGroup>

					<FormFieldGroup>
						<form.AppField
							name='phone'
							children={field => (
								<field.TextField
									label={L.Fields.Phone()}
									placeholder={L.Customers.Edit.PhonePlaceholder()}
								/>
							)}
						/>
					</FormFieldGroup>

					<FormFieldGroup>
						<form.AppField
							name='ssn'
							children={field => (
								<field.TextField
									label={L.Fields.SSN()}
									placeholder={L.Customers.Edit.SSNPlaceholder()}
									optional
								/>
							)}
						/>
					</FormFieldGroup>

					<FormFieldGroup>
						<form.AppField
							name='externalId'
							children={field => (
								<field.TextField
									label={L.Fields.ExternalId()}
									placeholder={L.Customers.Edit.ExternalIdPlaceholder()}
									optional
								/>
							)}
						/>
					</FormFieldGroup>
				</FormContainer>
			</Drawer.Body>
			<Drawer.Footer>
				<div className='flex items-center justify-end gap-x-2'>
					<Drawer.Close asChild>
						<Button variant='secondary' size='small'>
							{L.General.Cancel()}
						</Button>
					</Drawer.Close>
					<form.SaveButton />
				</div>
			</Drawer.Footer>
		</form.AppForm>
	)
}
